/** Picker order. */
export const SUPPORTED_CONTENT_LANGUAGES = [
    "en-US",
    "de-DE",
    "fr-FR",
    "pt-PT",
    "es-ES",
    "it-IT",
    "tr-TR",
    "da-DK",
];
export const DEFAULT_CONTENT_LANGUAGE = "en-US";
export const CONTENT_LANGUAGE_HEADER = "X-Content-Language";
/** Each language in its own name, for the picker. */
export const CONTENT_LANGUAGE_NATIVE_NAMES = {
    "en-US": "English",
    "de-DE": "Deutsch",
    "fr-FR": "Français",
    "pt-PT": "Português",
    "es-ES": "Español",
    "it-IT": "Italiano",
    "tr-TR": "Türkçe",
    "da-DK": "Dansk",
};
/** `de-DE` → `de`, for `<html lang>`. */
export const contentLanguagePrimarySubtag = (language) => language.split("-")[0];
let storage = null;
export const configureContentLanguageStorage = (adapter) => {
    storage = adapter;
};
/** Exact-match only: `de`, `DE-de`, `pl-PL` and non-strings are all unsupported. */
export const isContentLanguage = (value) => SUPPORTED_CONTENT_LANGUAGES.includes(value);
/**
 * The first of the browser's preferred languages we have, matched on the
 * primary subtag so `pt-BR` lands on `pt-PT` and `de-AT` on `de-DE`. The walk
 * stops at the first hit, English included: `["en-GB", "da"]` is English.
 * Null when none match.
 */
export const detectContentLanguage = (preferred) => {
    for (const tag of preferred) {
        const primary = tag.split("-")[0]?.toLowerCase();
        const match = SUPPORTED_CONTENT_LANGUAGES.find((language) => contentLanguagePrimarySubtag(language) === primary);
        if (match)
            return match;
    }
    return null;
};
let pageLanguage = DEFAULT_CONTENT_LANGUAGE;
/** The language this page renders in. Web passes the URL's locale, before the first request. */
export const configureContentLanguage = (language) => {
    pageLanguage = language;
};
/** Synchronous so the very first request can carry it. */
export const getContentLanguage = () => pageLanguage;
/**
 * The stored preference, or null when the user never picked. Key presence is
 * the "actively picked" flag: picking English writes "en-US" and counts as a
 * pick. Landing on a prefixed URL is not a pick.
 */
export const getStoredContentLanguage = () => {
    if (!storage)
        return null;
    try {
        const local = storage.get();
        return isContentLanguage(local) ? local : null;
    }
    catch {
        return null;
    }
};
/** Persists the preference only; the caller decides whether to reload or mirror to the profile. */
export const setContentLanguage = (language) => {
    try {
        storage?.set(language);
    }
    catch {
        // storage unavailable: the choice applies until the page is gone
    }
};
/**
 * Once logged in the account is the truth: mirrors the profile's language into
 * storage whenever it differs from the stored preference, so every browser on
 * the account follows the last explicit pick. Returns true when the profile
 * differs from the language this page renders in; the caller loads the page
 * again once under the profile's prefix. No loop: on that load the two match.
 */
export const adoptProfileContentLanguage = (profile) => {
    if (!storage || !isContentLanguage(profile))
        return false;
    const loadedWith = getContentLanguage();
    if (getStoredContentLanguage() !== profile)
        setContentLanguage(profile);
    return loadedWith !== profile;
};
/** Sends the page language on every request, anonymous or authenticated. */
export const attachContentLanguageHeader = (instance) => {
    instance.interceptors.request.use((config) => {
        config.headers[CONTENT_LANGUAGE_HEADER] = getContentLanguage();
        return config;
    });
    return instance;
};
//# sourceMappingURL=contentLanguage.js.map